import axios, { AxiosError, AxiosResponse } from "axios";
import { toast } from "react-toastify";
import { router } from "../router/Routes";
import { LoginRequest } from "../models/LoginRequest";
import { LoginResponse } from "../models/LoginResponse";
import { ApiResponse } from "../models/ApiResponse";
import { store } from "../stores/store";
import { baseApiUrl } from "./constants";
import { CreateOrderPost, OrderFilter } from "../services/OrderService";
import { CartPost, CartUpdatePost } from "../services/CartService";

axios.defaults.baseURL = baseApiUrl;
const responseBody = <T>(response: AxiosResponse<T>) => response.data;

axios.interceptors.request.use((config) => {
  const token = store.userStore.token;
  console.log("Token sent in request:", token);
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

axios.interceptors.response.use(
  async (response) => {
    return response;
  },
  (error: AxiosError) => {
    const { data, status, config } = error.response as AxiosResponse;
    switch (status) {
      case 400:
        if (config.method === "get" && data.errors.hasOwnProperty("id")) {
          router.navigate("/not-found");
        }
        if (data.errors) {
          const modalStateErrors = [];
          for (const key in data.errors) {
            if (data.errors[key]) {
              modalStateErrors.push(data.errors[key]);
            }
          }
          throw modalStateErrors.flat();
        } else {
          toast.error(data);
        }
        break;
      case 401:
        toast.error("unauthorised");
        break;
      case 403:
        toast.error("forbidden");
        break;
      case 404:
        router.navigate("/not-found");
        break;
      case 500:
        console.log(error);
        break;
    }
    return Promise.reject(error);
  }
);

const requests = {
  get: <T>(url: string) => axios.get<T>(url).then(responseBody),
  post: <T>(url: string, body: {}) =>
    axios.post<T>(url, body).then(responseBody),
  put: <T>(url: string, body: {}) => axios.put<T>(url, body).then(responseBody),
  del: <T>(url: string) => axios.delete<T>(url).then(responseBody),
};

const Product = {
  getAll: (
    filters: {
      category_id?: number;
      sub_category_id?: number;
      name?: string;
    } = {}
  ) => requests.post("/product", filters),
};

const Customer = {};

const User = {
  login: (credentials: LoginRequest) =>
    requests.post<ApiResponse<LoginResponse>>("/auth/login", credentials),
  current: () => requests.get("/auth/profile"),
  getProfile: () => requests.get<ApiResponse<any>>("/auth/profile"),
  forgotPassword: (email: string) =>
    requests.post<ApiResponse<any>>("/auth/forgot-password", { email }),
  resetPassword: (data: { email: string; otp: string; newPassword: string }) =>
    requests.post<ApiResponse<any>>("/auth/reset-password", data),
};

const Cart = {
  addToCart: (req: CartPost) => {
    return requests.post("/cart/add-to-cart", req);
  },
  getCart: (outlet_id: number) => {
    return requests.get(`/cart?outlet_id=${outlet_id}`);
  },
  updateQuantity: (req: CartUpdatePost) => requests.post("/cart/update", req),

  removeCart: (cart_id: number) => {
    return requests.del(`/cart/delete/${cart_id}`);
  },
  clearCart: (outlet_id:number) => {
    return requests.del(`/cart/clear?outlet_id=${outlet_id}`);
  },
  uploadExcel: (formData: FormData) => {
    return axios.post("/cart/upload-excel", formData, {
      headers: { "Content-Type": "multipart/form-data" }
    }).then(responseBody);
  },
};

const Orders = {
  getorders: (filters: OrderFilter) => requests.post("/orders", filters),
  createOrder: (req:CreateOrderPost) => requests.post(`/orders/create/`, req),
};

const Outlets = {
  getOutlets: () => requests.get<ApiResponse<any>>("/outlet"),
};

const agent = {
  Product,
  Customer,
  User,
  Cart,
  Orders,
  Outlets,
};

export default agent;
